import { LibraryPageContext } from "@/pages/library_v2/components/filesView/context";
import { type LibraryFile } from "@/pages/library_v2/components/filesView/types";
import { getShowFolderColumn } from "@/pages/library_v2/components/filesView/utils";

import { useContext, useMemo, useState } from "react";

import moment from "moment";
import { twMerge } from "tailwind-merge";

import {
  TABLE_GRID_NO_FOLDER,
  TABLE_GRID_WITH_FOLDER,
  TABLE_ROW_HEIGHT_PX,
} from "../tableHeaders";
import { FileActions } from "./actions";
import { FileName } from "./fileName";
import { FileStatusSelect } from "./fileStatusSelect";
import { PreviewFile } from "./previewFile";

type Props = {
  file: LibraryFile;
  isFirstRow?: boolean;
};

export const FileListTableRow: React.FC<Props> = ({ file, isFirstRow }) => {
  const tooltipPlacement = isFirstRow ? "bottom" : "top";
  const { selectedFolderName, selectedViewId, searchValue } =
    useContext(LibraryPageContext);

  const [isRenaming, setIsRenaming] = useState(false);
  const showFolderColumn = useMemo(
    () =>
      getShowFolderColumn({ selectedFolderName, selectedViewId, searchValue }),
    [selectedFolderName, selectedViewId, searchValue],
  );

  const folderName = useMemo(() => {
    return file.folderName || "";
  }, [file]);

  return (
    <div
      style={{ minHeight: TABLE_ROW_HEIGHT_PX }}
      className={twMerge(
        showFolderColumn ? TABLE_GRID_WITH_FOLDER : TABLE_GRID_NO_FOLDER,
        "group/row border-b border-[#F5EFEF] px-5 hover:bg-[#FBF8F8]",
      )}
    >
      <div className="flex min-w-0 items-center gap-1">
        <span className="text-grey6 w-[42px] flex-none rounded-[3px] bg-[#F1EAEA] py-1 text-center text-[9.5px] font-bold tracking-[0.04em] uppercase">
          {file.format}
        </span>
        <PreviewFile file={file} tooltipPlacement={tooltipPlacement} />
      </div>

      <FileName
        file={file}
        isRenaming={isRenaming}
        setIsRenaming={setIsRenaming}
      />

      {showFolderColumn && (
        <div className="min-w-0">
          {!!folderName && (
            <span className="border-grey2 inline-block max-w-full truncate rounded-[4px] border bg-[#F7F2F2] px-2 py-[3px] align-middle text-[11.5px] font-medium text-[#4a4747]">
              {folderName}
            </span>
          )}
        </div>
      )}

      <div className="text-[13px] text-[#4a4747]">
        {moment(file.date).format("DD MMM YYYY")}
      </div>

      <FileStatusSelect file={file} />

      <FileActions
        file={file}
        tooltipPlacement={tooltipPlacement}
        setIsRenaming={setIsRenaming}
      />
    </div>
  );
};
